import { JSX } from "react";
import { useRepositoriesContext } from "../../../../contexts/RepositoriesContext";
import { Design } from "../../../../core/models/entities/Design";
import { ErrorView } from "../../../components/error-view/ErrorView";
import {
  DesignDetails,
  DesignModalIntent,
} from "../components/design-details.component";
import { useDesignActionHandler } from "../hooks/useDesignActionHandler";

import { useApiClient } from "../../../../data/clients/ApiClient";
import { useQueryClient } from "@tanstack/react-query";
import { withToasts } from "../../../components/Toast/Toast";
import { useTranslation } from "react-i18next";
import { useProductPendingPoll } from "../../../../hooks/useProductPendingPoll";
import { actAsCacheKey } from "../../../../contexts/actAsStore";
import { isDesignActionLocked } from "../../../../utils/functions/productPending";

interface WrapperProps {
  design: Design;
  onClose: () => void;
  onShowRejectionNote: (design: Design) => void;
  closeDialog: () => void;
  intent: DesignModalIntent;
}

export function DesignDetailsWrapper({
  design,
  onClose,
  onShowRejectionNote,
  closeDialog,
  intent,
}: WrapperProps): JSX.Element {
  const { t } = useTranslation();
  const { designRepository } = useRepositoriesContext();
  const apiClient = useApiClient();
  const queryClient = useQueryClient();
  const {
    data: summary,
    isError: isSummaryError,
  } = designRepository.useDesign(design.customer.id, design.id);
  const { handleDeleteDesign, handleRejectAndDelete } =
    useDesignActionHandler();

  // Render optimistically with the design the list already loaded (incl. its
  // cached low-res image) so the modal paints INSTANTLY, and swap to the fresh
  // summary once the background fetch resolves. Previously the whole modal was
  // gated behind a spinner while getDesign ran (~1-1.6s), then requested a
  // not-yet-cached high-res image - the slow "t-shirt spinner". No extra
  // network: the list already fetched this design + its low-res preview.
  const shownDesign = summary ?? design;

  // "Prices on their way" (flag productPendingEnabled): while the fresh
  // summary says a placed logo's product is still owed by the API worker,
  // re-read this popup's own GetDesign entry (["designs", actAs, id]) at
  // 3/8/15 s - max 3 calls, never the list - so the badge turns into
  // prices without a reload. Judged on the summary, never the list stub.
  // "Preparing logo…" (flag cloneInstantEnabled): the same bounded re-read
  // while a placed clone is still preparing, so the locked Approve / Skip /
  // Send / Add to order open without closing the popup. Both flags off =
  // inert.
  const pricePending = isDesignActionLocked(summary);
  useProductPendingPoll(
    pricePending ? [["designs", actAsCacheKey(), design.id]] : undefined,
    pricePending
  );

  async function onDelete(design: Design) {
    try {
      await handleDeleteDesign(design);
      closeDialog();
    } catch (error) {
      console.error("Failed to delete design:", error);
    }
  }

  function onRejectAndDelete(design: Design, note?: string) {
    // Returned so the popup's verdict lock (flag approvalPatchEnabled) can
    // await it; the flag-off path ignores the value as before.
    return handleRejectAndDelete(design, note);
  }

  async function handleAssignCustomer(customerId: number) {
    const transaction = async () => {
      await apiClient.assignCustomerToDesign(
        design.customer.id,
        design.id,
        customerId
      );
      // The ["designs"] prefix already covers every design key (list AND the
      // detail keys, which now carry the act-as segment: ["designs", actAs,
      // id]) — a second, id-specific invalidation would need that segment and
      // adds nothing.
      await queryClient.invalidateQueries(["designs"]);
    };

    await withToasts(
      transaction,
      t("designListStyle.assigningCustomer"),
      t("designListStyle.customerAssigned"),
      t("designListStyle.assignCustomerFailed")
    );
  }

  // Only fall back to the error view when the background fetch failed AND we
  // never got fresh data - we still have the design the user clicked, so the
  // modal stays usable meanwhile.
  return isSummaryError && !summary ? (
    <div className={"w-220 h-128 relative"}>
      <ErrorView />
    </div>
  ) : (
    <DesignDetails
      // Remount when the fresh summary (incl. prices) arrives so useState
      // initializers (min quantity, remark) re-run against complete data -
      // the optimistic list-stub can lack prices and would otherwise freeze
      // the quantity default. Both images are cached by then, so the remount
      // repaints instantly.
      key={summary ? "summary" : "list-stub"}
      onShowRejectionNote={onShowRejectionNote}
      design={shownDesign}
      onClose={onClose}
      closeDialog={closeDialog}
      intent={intent}
      onDeleteDesign={onDelete}
      onRejectAndDelete={onRejectAndDelete}
      onAssignCustomer={handleAssignCustomer}
    />
  );
}
